import axios from 'axios';

/**
 * Creates authentication headers with Bearer token
 * @param extra Additional headers to merge
 * @throws Error if no token found in localStorage
 */
export function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');
  return { Authorization: `Bearer ${token}`, Accept: 'application/json', ...extra };
}

/**
 * Creates optional authentication headers (token is optional)
 * Used for public endpoints that work with or without auth
 */
export function optionalAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  const h: Record<string, string> = { Accept: 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

/**
 * Comprehensive error handler that normalizes axios errors
 * Handles AUTH_401, timeouts, network errors, and general HTTP errors
 */
export function asErr(prefix: string, err: unknown): Error {
  // Handle normalized AUTH_401 errors
  if ((err as any)?.code === "AUTH_401" || (err as any)?.message === "AUTH_401") {
    return new Error("AUTH_401");
  }
  
  if (axios.isAxiosError(err)) {
    // Handle timeout errors
    if (err.code === "ECONNABORTED" || err.message?.includes("timeout") || err.message?.includes("Timeout")) {
      return new Error("REQUEST_TIMEOUT");
    }
    
    // Handle network errors
    if (err.code === "ERR_NETWORK" || err.message?.includes("Network Error")) {
      return new Error("NETWORK_ERROR");
    }
    
    const status = err.response?.status;
    const body = err.response?.data as any;
    let msg: string;
    
    if (typeof body === "string") {
      msg = body.slice(0, 300);
    } else if (body && typeof body === "object") {
      msg = body.message || body.error || body.details || JSON.stringify(body);
    } else {
      msg = err.message || String(err);
    }
    
    // Use HTTP status format for consistency
    return new Error(`${prefix}${status ? ` (HTTP ${status})` : ""}: ${msg}`);
  }
  
  return new Error(`${prefix}: ${String(err)}`);
}

